/**
 * Equipment tiers for /technology/scanner-plotter-compatibility. Three tiers,
 * never blurred: `tested` means we own it and cut installed film from it;
 * `compatible` means the file format matches and nothing more; `ask` and `no`
 * are the honest remainder. Only move a device to `tested` when that is true.
 */
export type Tier = 'tested' | 'compatible' | 'ask' | 'no';

export interface Device {
	id: string;
	name: string;
	tier: Tier;
	/** One sentence shown in the checker result. */
	note: string;
}

export const scanners: Device[] = [
	{
		id: 'rockit',
		name: 'Einstar Rockit',
		tier: 'tested',
		note: 'One of the two scanners in our own studio. Its scans were flattened, cut and installed.'
	},
	{
		id: 'rigil',
		name: 'EinScan Rigil',
		tier: 'tested',
		note: 'One of the two scanners in our own studio. Its scans were flattened, cut and installed.'
	},
	{
		id: 'shining',
		name: 'Other Einstar / Shining 3D',
		tier: 'compatible',
		note: 'Exports STL, OBJ and PLY meshes, the same family as our studio scanners.'
	},
	{
		id: 'revopoint',
		name: 'Revopoint',
		tier: 'compatible',
		note: 'Exports a mesh PPF Engine can import. We have not run this scanner ourselves.'
	},
	{
		id: 'creality',
		name: 'Creality',
		tier: 'compatible',
		note: 'Exports a mesh PPF Engine can import. We have not run this scanner ourselves.'
	},
	{
		id: 'artec',
		name: 'Artec',
		tier: 'compatible',
		note: 'A professional class above our Rockit. Expect faster capture and less cleanup.'
	},
	{
		id: 'creaform',
		name: 'Creaform / Scantech',
		tier: 'compatible',
		note: 'Metrology-grade laser scanners. More than the job needs, and it will work.'
	},
	{
		id: 'phone',
		name: 'Phone / photogrammetry',
		tier: 'ask',
		note: 'Only works if the mesh has true metric scale, and small features like sensor bezels are often lost. Talk to us first.'
	},
	{
		id: 'other',
		name: 'Something else',
		tier: 'compatible',
		note: 'If its software exports STL, OBJ or PLY, the file will import. Tell us the model and we will say what we know.'
	},
	{
		id: 'none',
		name: 'No scanner yet',
		tier: 'ask',
		note: 'A handheld in the Einstar Rockit class is a good starting point. Anything in that class or better should serve you well.'
	}
];

export const plotters: Device[] = [
	{
		id: 'fc9000',
		name: 'Graphtec FC9000',
		tier: 'tested',
		note: 'This is the plotter in our own studio. Export is developed against it and cut on it in production.'
	},
	{
		id: 'graphtec',
		name: 'Other Graphtec',
		tier: 'compatible',
		note: 'Reads PLT (HP-GL) and the vector files Graphtec software imports.'
	},
	{
		id: 'summa',
		name: 'Summa',
		tier: 'compatible',
		note: 'Its cutting software imports SVG or DXF. We have not cut on one ourselves.'
	},
	{
		id: 'roland',
		name: 'Roland',
		tier: 'compatible',
		note: 'Its cutting software imports SVG or DXF. We have not cut on one ourselves.'
	},
	{
		id: 'mimaki',
		name: 'Mimaki',
		tier: 'compatible',
		note: 'Its cutting software imports SVG or DXF. We have not cut on one ourselves.'
	},
	{
		id: 'other',
		name: 'Something else',
		tier: 'compatible',
		note: 'If you can open an SVG, DXF or PLT in the software that drives it, you can cut the pattern.'
	},
	{
		id: 'locked',
		name: 'Locked to a pattern subscription',
		tier: 'no',
		note: 'A cutter that only accepts one vendor’s patterns and has no file import cannot receive outside files, from us or anyone else.'
	}
];

export const tierCopy: Record<Tier, { readout: string; headline: string }> = {
	tested: {
		readout: 'Tested',
		headline: 'This is exactly what we run. Tested end to end in our own studio.'
	},
	compatible: {
		readout: 'File-compatible',
		headline: 'The formats match, so this should work. We have not run this exact setup.'
	},
	ask: {
		readout: 'Talk to us first',
		headline: 'It depends. Tell us what you have and we will give you a straight answer.'
	},
	no: {
		readout: 'Not supported',
		headline: 'Outside files cannot reach this cutter, so this setup will not work today.'
	}
};

const SEVERITY: Tier[] = ['tested', 'compatible', 'ask', 'no'];

/** A setup is only as good as its weakest link. */
export function combinedTier(a: Tier, b: Tier): Tier {
	return SEVERITY[Math.max(SEVERITY.indexOf(a), SEVERITY.indexOf(b))];
}
